import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import Logo from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import GoogleG from "@/components/GoogleG";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", last_name: "", email: "", password: "" });
  const [loading, setLoading] = useState(false);

  const pw = form.password;
  const pwReq = {
    length: pw.length >= 6,
    digit: /\d/.test(pw),
    special: /[!@#$%^&*()\-_=+[\]{};:'",.<>/?\\|`~]/.test(pw),
  };
  const pwOk = pwReq.length && pwReq.digit && pwReq.special;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!pwOk) { toast.error("Le mot de passe ne respecte pas les critères de sécurité."); return; }
    setLoading(true);
    try {
      await register(form.email, form.password, form.name, form.last_name);
      toast.success("Compte créé !");
      navigate("/dashboard");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur d'inscription");
    } finally { setLoading(false); }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <Layout>
      <div className="max-w-md mx-auto px-4 py-16">
        <div className="flex justify-center mb-8"><Logo size={56} /></div>
        <div className="bg-white rounded-3xl p-8 border border-rose-100 shadow-[0_8px_30px_rgba(244,63,94,0.08)]">
          <h1 className="font-display text-2xl font-black text-stone-800 text-center" data-testid="register-title">Créer un compte famille</h1>

          <Button onClick={googleLogin} variant="outline" className="w-full mt-6 rounded-full py-6 border-2 border-stone-200 hover:bg-stone-50 font-bold" data-testid="register-google-button">
            <GoogleG size={20} className="mr-2" /> S'inscrire avec Google
          </Button>

          <div className="my-6 flex items-center gap-3">
            <div className="h-px bg-rose-100 flex-1" />
            <span className="text-xs text-stone-400 font-bold uppercase tracking-widest">ou</span>
            <div className="h-px bg-rose-100 flex-1" />
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <Label htmlFor="name">Prénom *</Label>
                <Input id="name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Sophie" className="rounded-2xl mt-1 py-6" data-testid="register-name-input" />
              </div>
              <div>
                <Label htmlFor="last_name">Nom * <span className="text-xs text-stone-400 font-normal">(privé)</span></Label>
                <Input id="last_name" required value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} placeholder="Martin" className="rounded-2xl mt-1 py-6" data-testid="register-lastname-input" />
              </div>
            </div>
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="sophie@famille.fr" className="rounded-2xl mt-1 py-6" data-testid="register-email-input" />
            </div>
            <div>
              <Label htmlFor="password">Mot de passe</Label>
              <Input id="password" type="password" required value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="rounded-2xl mt-1 py-6" data-testid="register-password-input" />
              <ul className="mt-2 text-xs bg-rose-50/60 rounded-xl p-3 space-y-1" data-testid="register-password-rules">
                <li className={`flex items-center gap-2 ${pwReq.length ? "text-emerald-600" : "text-stone-400"}`}>
                  <span className={`inline-block w-1.5 h-1.5 rounded-full ${pwReq.length ? "bg-emerald-500" : "bg-stone-300"}`}></span>
                  6 caractères minimum
                </li>
                <li className={`flex items-center gap-2 ${pwReq.digit ? "text-emerald-600" : "text-stone-400"}`}>
                  <span className={`inline-block w-1.5 h-1.5 rounded-full ${pwReq.digit ? "bg-emerald-500" : "bg-stone-300"}`}></span>
                  Au moins 1 chiffre
                </li>
                <li className={`flex items-center gap-2 ${pwReq.special ? "text-emerald-600" : "text-stone-400"}`}>
                  <span className={`inline-block w-1.5 h-1.5 rounded-full ${pwReq.special ? "bg-emerald-500" : "bg-stone-300"}`}></span>
                  Au moins 1 caractère spécial (!@#$%&*…)
                </li>
              </ul>
            </div>
            <Button type="submit" disabled={loading} className="w-full bg-orange-500 hover:bg-orange-600 rounded-full py-6 font-bold shadow-lg shadow-orange-500/30" data-testid="register-submit-button">
              {loading ? "Création..." : "Créer mon compte"}
            </Button>
            <p className="text-xs text-stone-500 text-center leading-relaxed">
              En créant un compte, vous acceptez nos{" "}
              <Link to="/cgu" className="text-orange-600 font-bold" data-testid="register-cgu-link">CGU</Link> et notre{" "}
              <Link to="/confidentialite" className="text-orange-600 font-bold" data-testid="register-privacy-link">politique de confidentialité</Link>.
            </p>
          </form>

          <p className="text-center text-sm text-stone-500 mt-6">
            Déjà inscrit ? <Link to="/connexion" className="text-orange-600 font-bold" data-testid="register-login-link">Se connecter</Link>
          </p>
        </div>
      </div>
    </Layout>
  );
}
